import React, { useId, useState } from 'react';
import { Controller } from 'react-hook-form';

import {
	Button,
	Grid,
	GridCol,
	Input,
	Popup,
	PopupSection,
	Select,
	SelectOption,
	Text,
	Textarea,
} from '@flixbus/honeycomb-react';

import OverlappingBanner from 'common/OverlappingActivitiesBanner/OverlappingBanner';

import RroWarningBanner from './RroWarningBanner';
import { useModalAbsence } from './useModalAbsence';

import styles from './styles.module.css';

const ModalAbsence = ({ readOnly = false, isEditMode = false, ...props }) => {
	const fromId = useId();
	const fromTimeId = useId();
	const tillId = useId();
	const tillTimeId = useId();
	const reasonId = useId();
	const noteId = useId();

	const isRRO = !readOnly && isEditMode && props.initialData?.isRRO;
	const [rroAcknowledged, setRroAcknowledged] = useState(false);
	const rroLocked = isRRO && !rroAcknowledged;

	const {
		control,
		miDay,
		minTillTime,
		maxTillTime,
		loading,
		reasonsLoading,
		reasons,
		isSubmitDisabled,
		isFromTimeDisabled,
		isTillDisabled,
		isTillTimeDisabled,
		hasOverlappingActivities,
		overlappingActivities,
		overlappingAction,
		cannotCancelOverlaps,
		isValidDate,
		validateYear,
		validateTillTime,
		handleFromDateChange,
		handleFromTimeChange,
		handleTillDateChange,
		handleTillTimeChange,
		setOverlappingAction,
		handleSubmit,
		handleClose,
	} = useModalAbsence({ readOnly, isEditMode, ...props });

	const title = readOnly ? 'Absence details' : isEditMode ? 'Edit absence' : 'Schedule absence';

	return (
		<Popup id="modal-absence" active={true} onOverlayClick={handleClose}>
			<PopupSection type="title">{title}</PopupSection>

			<PopupSection type="content">
				{!readOnly && (
					<Text>Please select a time frame and the reason of booking the crew member off.</Text>
				)}

				<form
					onSubmit={(e) => {
						e.preventDefault();
						handleSubmit();
					}}
				>
					<div className={styles.timeRow}>
						<div className={styles.timeWrapper}>
							<Controller
								name="from"
								control={control}
								rules={{
									required: true,
									validate: { year: validateYear, range: isValidDate },
								}}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={fromId}
										type="date"
										label="Start date and time"
										readOnly={readOnly}
										disabled={rroLocked}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										extraClasses={styles.dateInput}
										onChange={(e) => handleFromDateChange(field, e)}
									/>
								)}
							/>
							<Controller
								name="fromTime"
								control={control}
								rules={{ required: true }}
								disabled={isFromTimeDisabled}
								render={({ field: { ref: _ref, ...field } }) => (
									<Input
										{...field}
										id={fromTimeId}
										type="time"
										readOnly={readOnly}
										disabled={rroLocked}
										aria-label="Start time"
										extraClasses={styles.timeInput}
										onChange={(e) => handleFromTimeChange(field, e)}
									/>
								)}
							/>
						</div>

						<div className={styles.timeWrapper}>
							<Controller
								name="till"
								control={control}
								rules={{
									required: true,
									validate: { year: validateYear, range: isValidDate },
								}}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={tillId}
										type="date"
										min={miDay}
										disabled={isTillDisabled || rroLocked}
										readOnly={readOnly}
										label="End date and time"
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										extraClasses={styles.dateInput}
										onChange={(e) => handleTillDateChange(field, e)}
									/>
								)}
							/>
							<Controller
								name="tillTime"
								control={control}
								rules={{
									required: true,
									validate: validateTillTime,
								}}
								disabled={isTillTimeDisabled}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={tillTimeId}
										type="time"
										readOnly={readOnly}
										disabled={rroLocked}
										aria-label="End time"
										placeholder="hh:mm"
										min={minTillTime}
										max={maxTillTime}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										extraClasses={styles.timeInput}
										onChange={(e) => handleTillTimeChange(field, e)}
									/>
								)}
							/>
						</div>
					</div>

					<Grid gutterSize="4">
						<GridCol size={12} extraClasses={styles.gridItem}>
							<Controller
								name="reason"
								control={control}
								rules={{ required: true }}
								render={({ field: { ref: _ref, ...field } }) => (
									<Select
										{...field}
										id={reasonId}
										label="Reason"
										placeholder="Select a reason"
										disabled={reasonsLoading || readOnly || rroLocked}
										value={field.value?.id || field.value}
										onChange={(e) => field.onChange(e.target.value)}
									>
										{reasons.map((reason) => (
											<SelectOption key={reason.id} value={reason.id}>
												{reason.isPayable ? `$ ${reason.name}` : reason.name}
											</SelectOption>
										))}
									</Select>
								)}
							/>
						</GridCol>

						<GridCol size={12} extraClasses={styles.gridItem}>
							<Controller
								name="note"
								control={control}
								render={({ field: { ref: _ref, ...field } }) => (
									<Textarea
										{...field}
										id={noteId}
										label="Note"
										disabled={readOnly || rroLocked}
										placeholder={readOnly ? '' : 'Planned vacation as per contract'}
										onChange={(e) => field.onChange(e.target.value)}
									/>
								)}
							/>
						</GridCol>
					</Grid>

					{hasOverlappingActivities && (
						<OverlappingBanner
							overlappingList={overlappingActivities}
							overlappingAction={overlappingAction}
							onOverlappingActionChange={setOverlappingAction}
							cannotCancelOverlaps={cannotCancelOverlaps}
						/>
					)}

					{isRRO && (
						<RroWarningBanner
							acknowledged={rroAcknowledged}
							onChange={() => setRroAcknowledged((isRROAcknowledged) => !isRROAcknowledged)}
						/>
					)}
				</form>
			</PopupSection>

			<PopupSection type="actions" extraClasses={styles.gridItem}>
				<Button type="button" onClick={handleClose}>
					Cancel
				</Button>

				{!readOnly && (
					<Button
						type="submit"
						appearance="primary"
						disabled={isRRO ? !rroAcknowledged : isSubmitDisabled}
						loading={loading}
						onClick={handleSubmit}
					>
						Confirm
					</Button>
				)}
			</PopupSection>
		</Popup>
	);
};

export default ModalAbsence;
